import {
	ClientCarBodyTypeEnum,
	ClientCarFuelEnum,
	ClientPropertyCarVehicleTypeEnum,
	ClientPropertyVehicleTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { toBooleanGroup } from "web_myplann/Portfolio/Helpers/carBooleanGroups";
import { CAR_SECURITY_TYPES } from "web_myplann/Portfolio/Helpers/carSecurityTypes";
import { CAR_SPECIAL_EQUIPMENT } from "web_myplann/Portfolio/Helpers/carSpecialEquipment";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { optionalDateAsString } from "web_myplann/Shared/Validation/optionalDateAsString";
import { optionalPositiveFloat } from "web_myplann/Shared/Validation/optionalPositiveFloat";
import { optionalPositiveInteger } from "web_myplann/Shared/Validation/optionalPositiveInteger";
import { refineRequiredMinimum } from "web_myplann/Shared/Validation/refineRequiredMinimum";
import { trimmedNullable } from "web_myplann/Shared/Validation/trimmedNullable";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/i;

export const CAR_REQUIRED_MINIMUM_FIELDS = ["carBrand"] as const;

export const CAR_EXTENDED_FIELDS = [
	"carFuel",
	"carPower",
	"carVolume",
	"carBodyType",
	"carWeight",
	"carSeatCount",
	"carRegistration",
	"carPutIntoService",
	"carSerialNumber",
	"carMaxSpeed",
	"carSecurity",
	"carSpecialEquipment",
] as const;

export const getCarFormSchema = (t: TranslateFunction) => {
	const maxMessage = t(Resources.Portfolio.AddShared.Validations.maxValue);
	const positiveNumberMessages = {
		positiveMessage: t(
			Resources.Portfolio.AddShared.Validations.positiveNumber,
		),
		maxMessage,
	};
	const positiveIntegerMessages = {
		positiveMessage: t(Resources.Portfolio.AddCar.Validations.positiveInteger),
		maxMessage,
	};
	const notInFutureMessage = t(
		Resources.Portfolio.AddCar.Validations.notInFuture,
	);

	return z
		.object({
			carBrand: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Name,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.Name,
					}),
				),
			),
			carModel: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.LongName,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.LongName,
					}),
				),
			),
			propertyType: z.nativeEnum(ClientPropertyVehicleTypeEnum),
			carVehicleType: z.nativeEnum(ClientPropertyCarVehicleTypeEnum).nullable(),
			carSerialNumber: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Disposition,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.Disposition,
					}),
				),
			),
			carMaxSpeed: optionalPositiveFloat(
				ValidationLimits.Amount.MaxSpeed,
				positiveNumberMessages,
			),
			carFuel: z.nativeEnum(ClientCarFuelEnum).nullable(),
			carLicensePlate: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Code,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.Code,
					}),
				),
			),
			carVin: trimmedNullable(
				z
					.string()
					.refine(
						(val) => val === "" || VIN_PATTERN.test(val),
						t(Resources.Portfolio.AddCar.Validations.vinFormat),
					),
			),
			value: optionalPositiveFloat(
				ValidationLimits.Amount.Max,
				positiveNumberMessages,
			),
			carPurchaseDate: optionalDateAsString(DateFormat.Iso),
			carRegistration: optionalDateAsString(DateFormat.Iso, notInFutureMessage),
			carManufactureMonthYear: optionalDateAsString(
				DateFormat.MonthYear,
				notInFutureMessage,
			),
			carPower: optionalPositiveInteger(
				ValidationLimits.Amount.MaxPower,
				positiveIntegerMessages,
			),
			carVolume: optionalPositiveInteger(
				ValidationLimits.Amount.MaxVolume,
				positiveIntegerMessages,
			),
			carBodyType: z.nativeEnum(ClientCarBodyTypeEnum).nullable(),
			carWeight: optionalPositiveInteger(
				ValidationLimits.Amount.MaxWeight,
				positiveIntegerMessages,
			),
			carSeatCount: optionalPositiveInteger(
				ValidationLimits.Amount.MaxSeatCount,
				positiveIntegerMessages,
			),
			carPutIntoService: optionalDateAsString(DateFormat.Iso),
			carSecurity: z.array(z.string()),
			carSpecialEquipment: z.array(z.string()),
			description: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Description,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.Description,
					}),
				),
			),
			forInvestments: z.boolean(),
		})
		.superRefine(refineRequiredMinimum(CAR_REQUIRED_MINIMUM_FIELDS))
		.transform(({ carSecurity, carSpecialEquipment, ...rest }) => ({
			...rest,
			...toBooleanGroup(CAR_SECURITY_TYPES, carSecurity),
			...toBooleanGroup(CAR_SPECIAL_EQUIPMENT, carSpecialEquipment),
		}));
};

export type CarFormInput = z.input<ReturnType<typeof getCarFormSchema>>;
export type CarFormOutput = z.output<ReturnType<typeof getCarFormSchema>>;
